/**
 * How a verdict tier is coloured — in the header chip, the text view's value
 * gutter and the card values under it.
 *
 * Wave 5b §10 gives the built-ins a four-band vocabulary: **good** (every hurdle
 * met), **average** (we earn something, with less risk budget), **poor** (around
 * break-even, worth a closer look) and **kill** (the bid plus fees plus a basic
 * refurb exceeds the sale — buying a ready property would be cheaper). The
 * colours follow that meaning: green, informational blue, amber, red.
 *
 * That vocabulary is a **convention of the built-ins, not a schema enum** — an
 * author may name their tiers anything — so the mapping is by value first and by
 * POSITION second, never by a closed union. A model whose verdict reads
 * `works / watch / reject` still gets a green first arm and a red last one, and
 * a value no tier declares (or the compiler's own `not_computable`) is neutral
 * rather than guessed at.
 */

// The row model directly, not the DSL index: the lot page uses this mapping and must not load the CodeMirror language.
import { NOT_COMPUTABLE_TIER_VALUE, type OutcomeTier } from './dsl/rowModel'

/** The four decision bands, plus the neutral one for "no answer". */
export type VerdictTone = 'good' | 'average' | 'poor' | 'kill' | 'unknown'

/** The kit's verdict band for each tone, for a chip, a gutter value or a card value. */
const BAND: Record<VerdictTone, 'good' | 'avg' | 'poor' | 'kill' | 'na'> = {
  good: 'good',
  average: 'avg',
  poor: 'poor',
  kill: 'kill',
  unknown: 'na',
}

/** The built-ins' own vocabulary, which is the common case and reads exactly. */
const NAMED_TONES: Readonly<Record<string, VerdictTone>> = {
  good: 'good',
  average: 'average',
  poor: 'poor',
  kill: 'kill',
}

/**
 * Where a verdict sits on the good → kill scale.
 *
 * `tiers` is the model's own ordered list, which is what a model that does not
 * use the built-ins' words is judged by: first arm best, last arm worst.
 */
export function verdictTone(verdict: string | null | undefined, tiers: readonly OutcomeTier[] = []): VerdictTone {
  if (verdict === null || verdict === undefined || verdict.length === 0) return 'unknown'
  // The compiler owns this value and emits it when a required input is missing;
  // it is the absence of a verdict, not a bad one.
  if (verdict === NOT_COMPUTABLE_TIER_VALUE) return 'unknown'
  const named = NAMED_TONES[verdict]
  if (named !== undefined) return named
  const index = tiers.findIndex((tier) => tier.value === verdict)
  if (index < 0) return 'unknown'
  if (index === 0) return 'good'
  if (index === tiers.length - 1) return 'kill'
  return index === 1 ? 'average' : 'poor'
}

/** The kit `Verdict` band for a tone. */
export function verdictBand(tone: VerdictTone): 'good' | 'avg' | 'poor' | 'kill' | 'na' {
  return BAND[tone]
}
